import { authenticatedFetch } from '../utils/authErrorHandler';

interface UploadResult {
  filePath: string;
}

interface SASResponseData {
  sasUrl: string;
  filePath: string;
}

type StatusChangeCallback = (status: string) => void;

/**
 * Upload a markdown draft to the project's drafts folder using SAS token.
 * This will trigger the draft processing pipeline which adds it to user context.
 */
export async function uploadDraftToProject({
  content,
  filename,
  projectId,
  username,
  onStatusChange
}: {
  content: string;
  filename: string;
  projectId: string;
  username: string;
  onStatusChange?: StatusChangeCallback;
}): Promise<UploadResult> {
  if (!content || content.trim() === '') {
    throw new Error('Draft content cannot be empty');
  }

  if (!projectId) {
    throw new Error('No project available. Please wait for project to be initialized and try again.');
  }

  // Ensure filename ends with .md
  const draftFilename: string = filename.endsWith('.md') ? filename : `${filename}.md`;

  const fileServiceUrl: string | undefined = import.meta.env.VITE_FILE_SERVICE_BASE_URL;

  if (!fileServiceUrl) {
    throw new Error('VITE_FILE_SERVICE_BASE_URL environment variable is not configured');
  }

  // Step 1: Get SAS token for draft upload
  const sasUrl: string = `${fileServiceUrl}/sas/upload`;

  const sasRequest = {
    project_id: projectId,
    filename: draftFilename,
    content_type: 'text/markdown',
    folder: 'drafts',
  };

  onStatusChange?.('fetching_sas');

  const sasResponse: Response = await authenticatedFetch(sasUrl, {
    method: 'POST',
    body: JSON.stringify(sasRequest),
  });

  if (!sasResponse.ok) {
    let errorMessage: string = `Failed to get upload URL: ${sasResponse.status}`;
    try {
      const errorData: { detail?: string } = await sasResponse.json();
      if (errorData?.detail) {
        errorMessage = errorData.detail;
      }
    } catch (err) {
      // Ignore JSON parse errors and fall back to default message
    }
    throw new Error(errorMessage);
  }

  const sasData: SASResponseData = await sasResponse.json();

  // Step 2: Upload the markdown content as a blob using the SAS token
  onStatusChange?.('uploading');

  // Create a Blob from the markdown content
  const blob: Blob = new Blob([content], { type: 'text/markdown' });

  await new Promise<void>((resolve, reject) => {
    const xhr: XMLHttpRequest = new XMLHttpRequest();
    // Backend returns camelCase 'sasUrl' (Phase 4: no fallbacks)
    xhr.open('PUT', sasData.sasUrl, true);
    xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');
    xhr.setRequestHeader('Content-Type', 'text/markdown');

    // Add blob metadata headers to pass username to Azure Storage
    // For drafts, created_by is set initially and last_updated_by tracks edits
    if (username) {
      xhr.setRequestHeader('x-ms-meta-created_by', username);
      xhr.setRequestHeader('x-ms-meta-last_updated_by', username);
    }

    xhr.onload = (): void => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed: ${xhr.status} ${xhr.statusText}`));
      }
    };

    xhr.onerror = (): void => {
      reject(new Error('Network error during upload'));
    };

    xhr.send(blob);
  });

  onStatusChange?.('completed');

  return {
    filePath: sasData.filePath,
  };
}
